/**
 * Generador único de Excel para el panel de administración.
 *
 * Todas las descargas del panel pasan por acá para que salgan con el mismo
 * aspecto y, sobre todo, con los tipos de dato correctos: los montos son
 * números con formato moneda (se pueden sumar), las fechas son fechas reales
 * (se ordenan y filtran) y los porcentajes son porcentajes de Excel.
 *
 * Antes cada pantalla armaba su propio libro con SheetJS, que en su versión
 * community no permite estilos de celda: salían planillas sin encabezado
 * distinguible, con montos como texto y fechas como string dd/mm/yyyy.
 *
 * Uso típico:
 *
 *   await exportarLibro({
 *     archivo: 'talleres',
 *     titulo: 'Gestión de Talleres',
 *     usuario: user,
 *     filtros: { Recinto: 'Club Ñuñoa', Estado: 'activo' },
 *     hojas: [{
 *       nombre: 'Talleres',
 *       columnas: [
 *         { header: 'Nombre',  key: 'nombre',    tipo: 'texto',  ancho: 34 },
 *         { header: 'Cupos',   key: 'cupos',     tipo: 'numero' },
 *         { header: 'Ingresos', key: 'ingresos', tipo: 'moneda', total: true },
 *       ],
 *       filas: data.map(t => ({ nombre: t.nombre, cupos: t.cupos, ingresos: t.ingresos })),
 *     }],
 *   });
 */

// ── Paleta ────────────────────────────────────────────────────────────────────
// Azul institucional del panel (mismo colorPrimary que usa ConfigProvider).
const AZUL = 'FF0047BB';
const AZUL_SUAVE = 'FFF2F6FD'; // zebra
const AZUL_TOTAL = 'FFD9E4F7'; // fila de totales
const GRIS_BORDE = 'FFD9D9D9';
const GRIS_TEXTO = 'FF595959';

const VERDE_OK = 'FFE7F6E9';
const ROJO_ALERTA = 'FFFDE7E9';
const AMBAR = 'FFFFF4E0';

// ── Formatos numéricos ────────────────────────────────────────────────────────
const FORMATOS = {
  moneda: '"$"#,##0;[Red]-"$"#,##0',
  numero: '#,##0',
  decimal: '#,##0.0',
  porcentaje: '0.0%',
  fecha: 'dd-mm-yyyy',
  fechaHora: 'dd-mm-yyyy hh:mm',
};

const TIPOS_NUMERICOS = new Set(['moneda', 'numero', 'decimal', 'porcentaje']);

/**
 * Excel guarda el contenido en XML: un carácter de control cuela un XML
 * inválido y el archivo abre corrupto. Se limpian siempre.
 */
const limpiarTexto = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '';
  // eslint-disable-next-line no-control-regex -- se buscan justamente esos caracteres
  return String(valor).replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '').trim();
};

const aNumero = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = typeof valor === 'number' ? valor : Number(valor);
  return Number.isFinite(n) ? n : null;
};

const aFecha = (valor) => {
  if (!valor) return null;
  const d = valor instanceof Date ? valor : new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Convierte el valor crudo al tipo que Excel debe entender.
 * `porcentaje` recibe el número tal como viene de la API (85 = 85 %) y lo
 * divide por 100, porque el formato 0.0% de Excel trabaja sobre la fracción.
 */
const convertirValor = (valor, tipo) => {
  switch (tipo) {
    case 'moneda':
    case 'numero':
    case 'decimal':
      return aNumero(valor);
    case 'porcentaje': {
      const n = aNumero(valor);
      return n === null ? null : n / 100;
    }
    case 'fecha':
    case 'fechaHora':
      return aFecha(valor);
    case 'booleano':
      if (valor === null || valor === undefined || valor === '') return '';
      return valor ? 'Sí' : 'No';
    default: {
      const t = limpiarTexto(valor);
      return t === '' ? '' : t;
    }
  }
};

/** Ancho aproximado que ocupa el valor ya formateado, para calcular `!cols`. */
const anchoDeValor = (valor, tipo) => {
  if (valor === null || valor === undefined || valor === '') return 1;
  if (tipo === 'fecha') return 10;
  if (tipo === 'fechaHora') return 16;
  if (tipo === 'porcentaje') return 7;
  if (TIPOS_NUMERICOS.has(tipo)) {
    return Math.round(valor).toLocaleString('es-CL').length + (tipo === 'moneda' ? 2 : 0);
  }
  return String(valor).length;
};

/** Excel rechaza nombres de hoja con : \ / ? * [ ] o de más de 31 caracteres. */
const nombreHojaValido = (nombre, usados) => {
  let base = limpiarTexto(nombre).replace(/[:\\/?*[\]]/g, '-').slice(0, 31) || 'Hoja';
  let candidato = base;
  let i = 2;
  while (usados.has(candidato)) {
    const sufijo = ` (${i})`;
    candidato = base.slice(0, 31 - sufijo.length) + sufijo;
    i += 1;
  }
  usados.add(candidato);
  return candidato;
};

const bordeSuave = {
  top: { style: 'thin', color: { argb: GRIS_BORDE } },
  bottom: { style: 'thin', color: { argb: GRIS_BORDE } },
  left: { style: 'thin', color: { argb: GRIS_BORDE } },
  right: { style: 'thin', color: { argb: GRIS_BORDE } },
};

// ── Hoja de datos ─────────────────────────────────────────────────────────────
const construirHojaDatos = (workbook, hoja, usados) => {
  const { nombre, columnas = [], filas = [], notas = [] } = hoja;
  const sheet = workbook.addWorksheet(nombreHojaValido(nombre, usados), {
    views: [{ state: 'frozen', ySplit: 1 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });

  sheet.columns = columnas.map((c) => ({
    header: c.header,
    key: c.key,
    width: c.ancho || Math.max(String(c.header).length + 4, 12),
  }));

  // Encabezado
  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' }, name: 'Calibri' };
  headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL } };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  headerRow.height = 24;
  headerRow.eachCell((cell) => { cell.border = bordeSuave; });

  // Ancho automático a partir del contenido real (tope 55 para que no se dispare)
  const anchosCalculados = columnas.map((c) => String(c.header).length + 4);

  filas.forEach((fila, idx) => {
    const valores = {};
    columnas.forEach((c, ci) => {
      const bruto = typeof c.valor === 'function' ? c.valor(fila) : fila[c.key];
      const valor = convertirValor(bruto, c.tipo);
      valores[c.key] = valor;
      anchosCalculados[ci] = Math.max(anchosCalculados[ci], anchoDeValor(valor, c.tipo) + 2);
    });

    const row = sheet.addRow(valores);
    row.alignment = { vertical: 'middle' };

    if (idx % 2 === 1) {
      row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL_SUAVE } };
    }

    columnas.forEach((c) => {
      const cell = row.getCell(c.key);
      cell.border = bordeSuave;

      if (TIPOS_NUMERICOS.has(c.tipo)) {
        cell.numFmt = FORMATOS[c.tipo];
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      } else if (c.tipo === 'fecha' || c.tipo === 'fechaHora') {
        cell.numFmt = FORMATOS[c.tipo];
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      } else if (c.tipo === 'booleano') {
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      } else {
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: !!c.ajustarTexto };
      }

      // Semáforo opcional por celda: la pantalla decide, acá solo se pinta.
      if (typeof c.resaltar === 'function') {
        const estado = c.resaltar(fila);
        const color = estado === 'ok' ? VERDE_OK
          : estado === 'alerta' ? ROJO_ALERTA
            : estado === 'aviso' ? AMBAR : null;
        if (color) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
        }
      }
    });
  });

  // Fila de totales para las columnas marcadas con `total: true`
  const columnasConTotal = columnas.filter((c) => c.total && TIPOS_NUMERICOS.has(c.tipo));
  if (filas.length > 0 && columnasConTotal.length > 0) {
    const totales = {};
    totales[columnas[0].key] = 'TOTAL';
    columnasConTotal.forEach((c) => {
      totales[c.key] = filas.reduce((suma, fila) => {
        const bruto = typeof c.valor === 'function' ? c.valor(fila) : fila[c.key];
        const n = convertirValor(bruto, c.tipo);
        return suma + (typeof n === 'number' ? n : 0);
      }, 0);
    });

    const totalRow = sheet.addRow(totales);
    totalRow.font = { bold: true, size: 11 };
    totalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL_TOTAL } };
    totalRow.height = 20;
    columnas.forEach((c) => {
      const cell = totalRow.getCell(c.key);
      cell.border = { ...bordeSuave, top: { style: 'double', color: { argb: AZUL } } };
      if (TIPOS_NUMERICOS.has(c.tipo)) {
        cell.numFmt = FORMATOS[c.tipo];
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      }
    });
  }

  sheet.columns.forEach((col, i) => {
    col.width = Math.min(Math.max(anchosCalculados[i], 10), 55);
  });

  // El autofiltro se ancla solo al bloque de datos: si abarcara la fila TOTAL,
  // esa fila se ocultaría al filtrar y el usuario perdería el total a la vista.
  if (columnas.length > 0) {
    const ultimaFilaDatos = 1 + filas.length;
    sheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: Math.max(ultimaFilaDatos, 1), column: columnas.length },
    };
  }

  if (filas.length === 0) {
    const vacia = sheet.addRow({ [columnas[0]?.key || 'a']: 'Sin registros para los filtros aplicados' });
    vacia.font = { italic: true, color: { argb: GRIS_TEXTO } };
  }

  notas.filter(Boolean).forEach((nota, i) => {
    const row = sheet.getRow(sheet.rowCount + (i === 0 ? 2 : 1));
    row.getCell(1).value = nota;
    row.getCell(1).font = { italic: true, size: 9, color: { argb: GRIS_TEXTO } };
  });

  return sheet;
};

// ── Hoja tipo ficha (Métrica / Valor) ─────────────────────────────────────────
/**
 * Para resúmenes de indicadores. `metricas` es un array de
 * `{ metrica, valor, tipo }`; un item sin `valor` se dibuja como subtítulo.
 */
const construirHojaFicha = (workbook, hoja, usados) => {
  const { nombre, metricas = [] } = hoja;
  const sheet = workbook.addWorksheet(nombreHojaValido(nombre, usados), {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  sheet.columns = [
    { header: 'Métrica', key: 'metrica', width: 42 },
    { header: 'Valor', key: 'valor', width: 22 },
  ];

  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
  headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL } };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
  headerRow.height = 24;
  headerRow.eachCell((cell) => { cell.border = bordeSuave; });

  metricas.filter(Boolean).forEach((m) => {
    const esSubtitulo = m.valor === undefined;
    const row = sheet.addRow({
      metrica: limpiarTexto(m.metrica),
      valor: esSubtitulo ? '' : convertirValor(m.valor, m.tipo || 'texto'),
    });

    if (esSubtitulo) {
      row.font = { bold: true, color: { argb: AZUL } };
      row.getCell('metrica').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL_SUAVE } };
      row.getCell('valor').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL_SUAVE } };
      return;
    }

    row.getCell('metrica').border = bordeSuave;
    const celdaValor = row.getCell('valor');
    celdaValor.border = bordeSuave;
    celdaValor.font = { bold: true };
    if (TIPOS_NUMERICOS.has(m.tipo)) {
      celdaValor.numFmt = FORMATOS[m.tipo];
      celdaValor.alignment = { horizontal: 'right' };
    } else if (m.tipo === 'fecha' || m.tipo === 'fechaHora') {
      celdaValor.numFmt = FORMATOS[m.tipo];
      celdaValor.alignment = { horizontal: 'center' };
    }
  });

  return sheet;
};

// ── Portada ───────────────────────────────────────────────────────────────────
/**
 * Deja registro de qué es el archivo: sin esto un `ingresos-2026-08-12.xlsx`
 * no dice qué rango de fechas ni qué filtros lo generaron, y el reporte queda
 * imposible de auditar semanas después.
 */
const construirPortada = (workbook, { titulo, subtitulo, usuario, filtros, hojas }) => {
  const sheet = workbook.addWorksheet('Información del reporte', {
    views: [{ showGridLines: false }],
  });

  sheet.columns = [{ width: 34 }, { width: 52 }];

  sheet.mergeCells('A1:B1');
  const celdaTitulo = sheet.getCell('A1');
  celdaTitulo.value = titulo;
  celdaTitulo.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
  celdaTitulo.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL } };
  celdaTitulo.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  sheet.getRow(1).height = 34;

  sheet.mergeCells('A2:B2');
  const celdaSub = sheet.getCell('A2');
  celdaSub.value = subtitulo || 'Panel de administración · Talleres Ñuñoa';
  celdaSub.font = { size: 10, color: { argb: GRIS_TEXTO } };
  celdaSub.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  sheet.getRow(2).height = 20;

  let fila = 4;
  const agregarSeccion = (texto) => {
    const row = sheet.getRow(fila);
    row.getCell(1).value = texto;
    row.getCell(1).font = { bold: true, size: 11, color: { argb: AZUL } };
    row.getCell(1).border = { bottom: { style: 'thin', color: { argb: AZUL } } };
    row.getCell(2).border = { bottom: { style: 'thin', color: { argb: AZUL } } };
    fila += 1;
  };
  const agregarDato = (etiqueta, valor, tipo) => {
    if (valor === null || valor === undefined || valor === '') return;
    const row = sheet.getRow(fila);
    row.getCell(1).value = etiqueta;
    row.getCell(1).font = { color: { argb: GRIS_TEXTO } };
    const celda = row.getCell(2);
    celda.value = convertirValor(valor, tipo || 'texto');
    celda.font = { bold: true };
    if (tipo && FORMATOS[tipo]) celda.numFmt = FORMATOS[tipo];
    fila += 1;
  };

  agregarSeccion('Generación');
  agregarDato('Fecha y hora de descarga', new Date(), 'fechaHora');
  agregarDato('Generado por', usuario?.name || usuario?.email || 'No identificado');
  agregarDato('Rol', usuario?.role ? usuario.role.charAt(0).toUpperCase() + usuario.role.slice(1) : null);
  fila += 1;

  const entradasFiltros = Object.entries(filtros || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '');
  agregarSeccion('Filtros aplicados');
  if (entradasFiltros.length === 0) {
    agregarDato('Filtros', 'Ninguno · datos completos');
  } else {
    entradasFiltros.forEach(([k, v]) => agregarDato(k, v));
  }
  fila += 1;

  agregarSeccion('Contenido del archivo');
  hojas.forEach((h) => {
    const cantidad = h.filas ? h.filas.length : (h.metricas || []).filter((m) => m && m.valor !== undefined).length;
    const unidad = h.filas ? (cantidad === 1 ? 'registro' : 'registros') : (cantidad === 1 ? 'métrica' : 'métricas');
    agregarDato(h.nombre, `${cantidad.toLocaleString('es-CL')} ${unidad}`);
  });

  fila += 1;
  const pie = sheet.getRow(fila);
  pie.getCell(1).value = 'Los montos están en pesos chilenos. Documento generado automáticamente; no requiere firma.';
  pie.getCell(1).font = { italic: true, size: 9, color: { argb: GRIS_TEXTO } };
  sheet.mergeCells(fila, 1, fila, 2);

  return sheet;
};

// ── Descarga ──────────────────────────────────────────────────────────────────
const descargarBuffer = (buffer, nombreArchivo) => {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nombreArchivo);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const fechaArchivo = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * Punto de entrada único.
 *
 * @param {object}   opciones
 * @param {string}   opciones.archivo   Nombre base, sin fecha ni extensión.
 * @param {string}   opciones.titulo    Título que va en la portada.
 * @param {string}  [opciones.subtitulo]
 * @param {object}  [opciones.usuario]  El `user` del provider de Auth.
 * @param {object}  [opciones.filtros]  { etiqueta: valor } de los filtros activos.
 * @param {Array}    opciones.hojas     Hojas de datos (`columnas`+`filas`) o ficha (`metricas`).
 * @param {boolean} [opciones.portada=true]
 */
export const exportarLibro = async ({
  archivo,
  titulo,
  subtitulo,
  usuario,
  filtros,
  hojas = [],
  portada = true,
}) => {
  const hojasValidas = hojas.filter(Boolean);
  if (hojasValidas.length === 0) {
    throw new Error('No hay datos para exportar');
  }

  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Panel de administración · Talleres Ñuñoa';
  workbook.lastModifiedBy = usuario?.name || usuario?.email || 'Panel de administración';
  workbook.created = new Date();
  workbook.modified = new Date();

  if (portada) {
    construirPortada(workbook, { titulo, subtitulo, usuario, filtros, hojas: hojasValidas });
  }

  const usados = new Set(['Información del reporte']);
  hojasValidas.forEach((hoja) => {
    if (hoja.metricas) construirHojaFicha(workbook, hoja, usados);
    else construirHojaDatos(workbook, hoja, usados);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  descargarBuffer(buffer, `${archivo}-${fechaArchivo()}.xlsx`);
};

/**
 * Atajo para el caso más común: una sola hoja de datos.
 */
export const exportarHojaUnica = async ({
  archivo, titulo, subtitulo, usuario, filtros, nombreHoja, columnas, filas, notas,
}) =>
  exportarLibro({
    archivo,
    titulo,
    subtitulo,
    usuario,
    filtros,
    hojas: [{ nombre: nombreHoja, columnas, filas, notas }],
  });

export const COLORES_EXPORT = { AZUL, AZUL_SUAVE, AZUL_TOTAL, VERDE_OK, ROJO_ALERTA, AMBAR };

const ExportExcel = { exportarLibro, exportarHojaUnica, COLORES_EXPORT };

export default ExportExcel;
